import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  getIdTokenResult,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { useAppDispatch } from "@/hooks";
import { auth } from "@/lib/firebase";
import { setIsApiLoading, setUser } from "@/store/appSlice";
import Footer from "@/components/layouts/Footer";

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

const loginSchema: yup.ObjectSchema<LoginFormValues> = yup
  .object({
    email: yup
      .string()
      .required("メールアドレスを入力してください")
      .matches(
        /^(?=.{1,64}$)[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/,
        "正しいメールアドレスを入力してください",
      ),
    password: yup
      .string()
      .required("パスワードを入力してください")
      .min(8, "8文字以上入力してください"),
    remember: yup.boolean().default(false),
  })
  .required();

const inputBaseClass =
  "w-full rounded-md border border-transparent bg-surface-200 px-4 py-3 text-base text-text-main placeholder:text-text-muted transition focus:bg-surface-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1";

const Login = () => {
  const dispatch = useAppDispatch();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      remember: false,
    },
  });

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  const handleLogin = async (data: LoginFormValues) => {
    dispatch(setIsApiLoading(true));
    try {
      const { email, password, remember } = data;
      const persistenceType = remember ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(auth, persistenceType);

      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const tokenResult = await getIdTokenResult(credential.user);
      const claims = tokenResult.claims;

      dispatch(
        setUser({
          uid: credential.user.uid,
          email: credential.user.email ?? "",
          displayName: credential.user.displayName ?? "",
          photoURL: credential.user.photoURL ?? "",
          claims,
        }),
      );

      // Check if user is paused
      const isPaused = claims.isPause === 1 || claims.isPause === true;
      window.location.href = isPaused ? "/suspended" : "/home";
    } catch {
      setError("password", {
        type: "server",
        message: "メールアドレスかパスワードが間違っています",
      });
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const inputErrorClass = "border-red-400 focus:ring-red-400";

  return (
    <>
      <Helmet>
        <title>ログイン</title>
      </Helmet>
      <div className="relative flex min-h-screen flex-col items-center overflow-hidden bg-gradient-custom px-4 py-12 text-white">
        <div className="absolute inset-x-0 top-6 flex justify-center">
          <a href="/" className="inline-flex">
            <img src="/images/logoLogin.svg" alt="STUDICT" className="h-12 w-auto drop-shadow" />
          </a>
        </div>

        <div className="relative mt-24 flex w-full max-w-5xl flex-1 flex-col items-center pb-32">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit(handleLogin)(e);
            }}
            className="w-full max-w-[420px] overflow-hidden rounded-xl bg-white text-text-main shadow-card"
          >
            <div className="flex items-center justify-center bg-gradient-to-r from-gradient-start to-gradient-end py-3">
              <h2 className="text-center font-sans text-base font-bold tracking-wide text-white">
                ログイン
              </h2>
            </div>

            <div className="flex flex-col gap-6 px-7 py-8">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-text-main select-none">
                  メールアドレス
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="例 info@studict.com"
                  className={`${inputBaseClass} ${errors.email ? inputErrorClass : ""}`}
                  aria-invalid={errors.email ? "true" : "false"}
                  {...register("email")}
                />
                {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="text-sm font-medium text-text-main select-none"
                >
                  パスワード
                </label>
                <div className="relative">
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    placeholder="半角英数記号"
                    className={`${inputBaseClass} pr-12 ${errors.password ? inputErrorClass : ""}`}
                    aria-invalid={errors.password ? "true" : "false"}
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    aria-label={isPasswordVisible ? "パスワードを隠す" : "パスワードを表示"}
                    className="absolute inset-y-0 right-3 flex items-center justify-center"
                  >
                    <img
                      src={
                        isPasswordVisible ? "/images/icon_openEyes.svg" : "/images/icon_eyes.svg"
                      }
                      alt=""
                      className="h-6 w-6"
                    />
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-center gap-4">
                <div className="flex cursor-pointer items-center gap-3 text-sm font-medium text-text-main">
                  <div className="flex w-6 h-6 flex-col justify-center items-center gap-2.5 flex-shrink-0 cursor-pointer">
                    <input
                      id="remember"
                      type="checkbox"
                      className="hidden peer"
                      {...register("remember")}
                    />
                    <label
                      htmlFor="remember"
                      className="w-[22px] h-[22px] flex justify-center items-center border border-[#ccc] bg-[#f0f0f0] rounded-[3px] transition-all duration-200 ease-in-out relative peer-checked:bg-gradient-to-r peer-checked:from-[#1bd3cc] peer-checked:to-[#2a51d6] peer-checked:border-transparent cursor-pointer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="23"
                        viewBox="0 0 22 23"
                        fill="none"
                        className="absolute transition-opacity duration-200 ease-in-out opacity-0 peer-checked:opacity-100"
                      >
                        <rect
                          y="0.802734"
                          width="22"
                          height="22"
                          rx="3"
                          fill="url(#paint0_linear_2671_14693)"
                        />
                        <path
                          d="M5.5 11.4715L9.625 15.184L16.5 8.58398"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <defs>
                          <linearGradient
                            id="paint0_linear_2671_14693"
                            x1="0.160254"
                            y1="-2.86393"
                            x2="22.071"
                            y2="-2.81468"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#1BD3CC" />
                            <stop offset="1" stopColor="#2A51D6" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </label>
                  </div>
                  <label htmlFor="remember" className="cursor-pointer select-none">
                    自動ログインを有効
                  </label>
                </div>
              </div>
              <button
                type="submit"
                className="mt-4 mx-auto inline-flex w-[80%] items-center justify-center rounded-full bg-gradient-to-r from-gradient-start to-gradient-end px-6 py-3 font-sans text-lg font-semibold text-white shadow-md transition hover:opacity-80"
              >
                ログイン
              </button>
              <a
                href="/forgot-password"
                className="text-sm font-medium text-primary hover:text-primary-dark"
              >
                パスワードを忘れた場合
              </a>
            </div>
          </form>
          <Footer className="absolute bottom-0 left-0 right-0" />
        </div>
      </div>
    </>
  );
};

export default Login;
